import _ from 'lodash';
import { rgba } from 'polished';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useWorkspace } from '@treams/treams-hooks';
import { WarningIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Heading3,
	Label,
	Paragraph,
	Separator,
	Spinner,
	Switch,
	tokens,
} from '@treams/treams-ui';

const Container = styled(Box)<{ enabled?: boolean }>`
	border: 1px solid ${tokens.border.subtle};
	border-radius: 5px;

	${(props) =>
		props.enabled &&
		`
        border: 1px solid ${rgba(tokens.text.secondary, 0.25)};
        background: ${props.theme.colors.white};
        box-shadow: 0px 2px 4px ${rgba(tokens.text.secondary, 0.1)},
            0px 1px 2px ${rgba(tokens.text.secondary, 0.05)};
    `}
`;

const Grid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 0 16px;
`;

function IdentityProvider(props: {
	readOnly?: boolean;
	user?: graphql.User;
	username: string;
	email: string;
	provider: graphql.IdentityProvider;
	associatedIdentities: { identityProviderID: string; name: string }[];
	setAssociatedIdentities(identities: { identityProviderID: string; name: string }[]): void;
}) {
	const { t } = useTranslation();

	const after = props.associatedIdentities;

	const identities = [
		{
			identityProviderID: props.provider.id,
			name: props.username,
		},
		{
			identityProviderID: props.provider.id,
			name: props.email,
		},
	];

	const [warning, setWarning] = useState(
		props.associatedIdentities.length > 0 &&
			!_.isEqual(new Set(identities), new Set(props.associatedIdentities)),
	);

	useEffect(() => {
		if (after.length === 0 || warning) {
			return;
		}

		props.setAssociatedIdentities(identities);
	}, [props.username, props.email, warning]);

	return (
		<Box>
			<Box p={3}>
				<Flex alignItems="center">
					<Box flexGrow={1} flexShrink={1} mb={3}>
						<Heading3 mb={0}>{t('settings.employees.authentication.sso.title')}</Heading3>
						<Label>
							{props.provider.configuration?.name ||
								t('settings.integrations.sso.id', { id: props.provider.id })}
						</Label>
					</Box>
				</Flex>
				<Flex alignItems="center">
					<Paragraph>{t('settings.employees.authentication.sso.description')}</Paragraph>
					{warning ? (
						<Box ml={3} flexGrow={0} flexShrink={0}>
							<Button
								small
								type="destructive"
								icon={<WarningIcon />}
								title={t('settings.employees.authentication.sso.fix')}
								disabled={props.readOnly}
								onClick={() => setWarning(false)}
							/>
						</Box>
					) : (
						props.readOnly !== true && (
							<Box ml={3} flexGrow={0} flexShrink={0}>
								<Switch
									on={after.length > 0}
									onChange={() => {
										if (after.length === 0) {
											props.setAssociatedIdentities(identities);
										} else {
											props.setAssociatedIdentities([]);
										}
									}}
								/>
							</Box>
						)
					)}
				</Flex>
			</Box>
			<Separator />
		</Box>
	);
}

function AssociatedIdentitiesSupport(props: {
	readOnly?: boolean;
	associatedIdentities: { identityProviderID: string; name: string }[];
	setAssociatedIdentities(identities: { identityProviderID: string; name: string }[]): void;
}) {
	const { t } = useTranslation();

	const { data } = graphql.useSupportEmailAddressesQuery();

	const users = data?.supportEmailAddresses ?? [];

	return (
		<Box>
			<Flex alignItems="center">
				<Box flexGrow={1}>
					<Box mb={2}>
						<Heading3 mb={0}>{t('settings.employees.authentication.sso.title')}</Heading3>
					</Box>
					<Paragraph>{t('settings.employees.authentication.sso.description')}</Paragraph>
				</Box>
			</Flex>
			<Grid mt={2}>
				{props.readOnly
					? users
							.filter((user) => props.associatedIdentities.some(({ name }) => name === user))
							.map((user) => (
								<Flex key={user} alignItems="center" mt={1}>
									<Box flexGrow={1} flexShrink={1}>
										{user}
									</Box>
								</Flex>
							))
					: users.map((user) => (
							<Flex key={user} alignItems="center" mt={1}>
								<Box flexGrow={1} flexShrink={1}>
									{user}
								</Box>
								<Box ml={3} flexGrow={0} flexShrink={0}>
									<Switch
										on={props.associatedIdentities.some(({ name }) => name === user)}
										onChange={() => {
											if (props.associatedIdentities.some(({ name }) => name === user)) {
												props.setAssociatedIdentities(
													props.associatedIdentities.filter(({ name }) => name !== user),
												);
											} else {
												props.setAssociatedIdentities(
													props.associatedIdentities.concat([
														{
															identityProviderID: 'support',
															name: user,
														},
													]),
												);
											}
										}}
									/>
								</Box>
							</Flex>
						))}
			</Grid>
		</Box>
	);
}

export default function AssociatedIdentities(props: {
	readOnly?: boolean;
	user?: graphql.User;
	username: string;
	email: string;
	associatedIdentities: { identityProviderID: string; name: string }[];
	setAssociatedIdentities(identities: { identityProviderID: string; name: string }[]): void;
}) {
	const query = graphql.useIdentityProvidersQuery();
	const workspace = useWorkspace();

	if (!query.data) {
		return <Spinner centered />;
	}

	const filteredProviders = props.readOnly
		? query.data.identityProviders.filter((provider) =>
				props.associatedIdentities.some((identity) => identity.identityProviderID === provider.id),
			)
		: query.data.identityProviders;

	return (
		<Container enabled={props.associatedIdentities.length > 0}>
			{props.username === 'support' || workspace.id === 'launch' ? (
				<Box p={3}>
					<AssociatedIdentitiesSupport
						readOnly={props.readOnly}
						associatedIdentities={props.associatedIdentities}
						setAssociatedIdentities={props.setAssociatedIdentities}
					/>
				</Box>
			) : (
				filteredProviders.map((provider) => (
					<IdentityProvider
						key={provider.id}
						readOnly={props.readOnly}
						user={props.user}
						username={props.username}
						email={props.email}
						associatedIdentities={props.associatedIdentities.filter(
							(identity) => identity.identityProviderID === provider.id,
						)}
						setAssociatedIdentities={(identities) => {
							const others = props.associatedIdentities.filter(
								(identity) => identity.identityProviderID !== provider.id,
							);

							props.setAssociatedIdentities(others.concat(identities));
						}}
						provider={provider}
					/>
				))
			)}
		</Container>
	);
}
